import React, {useEffect, useRef} from 'react';
import {NavLink} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {OverridableComponent} from '@mui/material/OverridableComponent';
import {SvgIconTypeMap} from '@mui/material/SvgIcon';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../store';
import {toggleSidebar} from '../../store/slices/sidebarSlice';

export type SidebarItem = {
  labelKey: string;
  to: string;
  icon?: OverridableComponent<SvgIconTypeMap> & { muiName: string };
  rightSideBar?: boolean;
};

type Props = {
  items: SidebarItem[];
  titleKey: string;
  setSidebarWidth: (width: number) => void;
  isSystem?: boolean;
};

const Sidebar: React.FC<Props> = ({items, titleKey, setSidebarWidth, isSystem}) => {
  const dispatch = useDispatch();
  const collapsed = useSelector((state: RootState) => state.sidebar.collapsed);
  const {t} = useTranslation();

  const asideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!asideRef.current) return;

    const updateWidth = () => {
      if (asideRef.current) {
        setSidebarWidth(asideRef.current.offsetWidth);
      }
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(asideRef.current);

    return () => {
      observer.disconnect();
    };
  }, [setSidebarWidth]);

  // Handle system specific styles
  const sidebarBaseClass = isSystem
      ? 'bg-light-primary dark:bg-dark-stronger border-r border-blue-400/20 text-white'
      : 'bg-white dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-700';

  const titleClass = isSystem
      ? 'text-white'
      : 'text-light-text dark:text-dark-text';

  const toggleBtnClass = isSystem
      ? 'text-blue-100 hover:text-white'
      : 'text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white';

  return (
      <aside
          ref={asideRef}
          className={`
        h-auto min-h-screen lg:min-h-[94vh] ${sidebarBaseClass} 
        py-0 md:py-4 px-1 md:px-4
        transition-[width] duration-500 ease-in-out
        ${collapsed ? 'w-10 md:w-16' : 'w-64'}
      `}
      >
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'} mt-4 mb-6`}>
          <div
              className={`
            overflow-hidden transition-all duration-200
            ${collapsed ? 'opacity-0 scale-95 w-0' : 'opacity-100 scale-100 delay-300 w-auto'}
          `}
          >
            <h2 className={`text-xl font-bold whitespace-nowrap ${titleClass}`}>
              {t(titleKey)}
            </h2>
          </div>

          <button
              onClick={() => dispatch(toggleSidebar())}
              className={`transition-colors duration-300 ${toggleBtnClass}`}
          >
            {collapsed ? <ArrowForwardIosIcon fontSize="medium"/> : <ArrowBackIosIcon fontSize="medium"/>}
          </button>
        </div>

        <nav className="flex flex-col gap-2">
          {items.map(({labelKey, to, icon: Icon}) => (
              <NavLink
                  key={to}
                  to={to}
                  className={({isActive}) => {
                    // Internal logic for conditional styles based on system-mode and active-state
                    let styles = 'flex items-center font-medium py-2 rounded-lg transition-all duration-200 ';
                    styles += collapsed ? 'justify-center px-0' : 'justify-start gap-3 px-4';

                    if (isSystem) {
                      // Styles for System-Sidebar (Blue Theme)
                      styles += isActive
                          ? ' bg-white/20 text-white shadow-sm'
                          : ' text-blue-50 hover:bg-white/10 hover:text-white';
                    } else {
                      // Styles for Standard-Sidebar
                      styles += isActive
                          ? ' bg-blue-100 text-light-primary dark:bg-dark-primary dark:text-white'
                          : ' text-gray-700 dark:text-dark-text hover:bg-neutral-100 dark:hover:bg-neutral-800';
                    }
                    return styles;
                  }}
              >
                {Icon && (
                    <div className="w-6 h-6 flex items-center justify-center">
                      <Icon fontSize="small"/>
                    </div>
                )}
                <span
                    className={`
                transition-all duration-200 whitespace-nowrap
                ${collapsed ? 'opacity-0 scale-95 w-0 overflow-hidden' : 'opacity-100 scale-100 delay-300'}
              `}
                >
              {t(labelKey)}
            </span>
              </NavLink>
          ))}
        </nav>
      </aside>
  );
};

export default Sidebar;